import ChallengesService from "@/api/services/challenges";
import { toCamelCase } from "@estaly/ui";

// Last request started by each fetch action, so that a late response (e.g. of a previously selected store)
// does not overwrite the challenge of a more recent one
const latestRequests = { activeChallenge: 0, activePerformance: 0 };

const startRequest = (name) => {
  const request = ++latestRequests[name];
  return () => request !== latestRequests[name];
};

// Challenge running in the store among its challenges, or null; undefined when the request fails
async function findActiveChallenge(storeId, { includes = [] } = {}) {
  const response = await ChallengesService.fetchIndex(storeId, { includes, perPage: 2000 });
  if (!response?.success) return undefined;

  return toCamelCase(response.data).challenges?.find((challenge) => challenge.active) ?? null;
}

export default {
  namespaced: true,

  state: {
    hasActiveChallenge: false,
    // Challenge running in the selected store, with its sales targets (light, for the POS home)
    activeChallenge: null,
    // Challenge running in the selected store, with its performance (the challenge follow-up): { challenge, performance }
    activePerformance: null,
  },

  getters: {
    hasActiveChallenge: (state) => state.hasActiveChallenge,
  },

  mutations: {
    setHasActiveChallenge(state, value) {
      state.hasActiveChallenge = value;
    },
    SET_ACTIVE_CHALLENGE(state, activeChallenge) {
      state.activeChallenge = activeChallenge;
    },
    SET_ACTIVE_PERFORMANCE(state, activePerformance) {
      state.activePerformance = activePerformance;
    },
  },

  // Both fetch actions clear the challenge of the previously selected store first, so it is never shown for
  // another store or when no store is selected
  actions: {
    async checkActiveChallenge({ commit }) {
      const response = await ChallengesService.fetchIndex();
      if (!response?.success) return;

      const data = toCamelCase(response.data);
      const hasActive = data.challenges?.some((challenge) => challenge.active) || false;
      commit("setHasActiveChallenge", hasActive);
    },

    async fetchActiveChallenge({ commit }, storeId) {
      const isStale = startRequest("activeChallenge");
      commit("SET_ACTIVE_CHALLENGE", null);
      if (!storeId) return;

      const activeChallenge = await findActiveChallenge(storeId, { includes: ["sales_targets"] });
      if (isStale() || !activeChallenge) return;

      commit("SET_ACTIVE_CHALLENGE", activeChallenge);
    },

    async fetchActivePerformance({ commit }, storeId) {
      const isStale = startRequest("activePerformance");
      commit("SET_ACTIVE_PERFORMANCE", null);
      if (!storeId) return;

      const activeChallenge = await findActiveChallenge(storeId);
      if (isStale() || !activeChallenge) return;

      const response = await ChallengesService.fetchPerformance(storeId, activeChallenge.id);
      if (isStale() || !response?.success) return;

      const { challenge, performance } = toCamelCase(response.data);
      commit("SET_ACTIVE_PERFORMANCE", { challenge, performance });
    },
  },
};
